import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useOpenBalanceContext } from "../context/OpenBalanceContext";
import { OpenBalanceTexts } from "../utils/openBalance-enums";
import { getReplacementText, redirectToOrders } from "../utils/openBalance-helpers";
// Custom hook to handle payment completion logic, including countdown and redirection
export const useRedirectCountdown = () => {
    const OBP = useOpenBalanceContext();
    const navigate = useNavigate(); // React Router's navigation hook
    const [countdown, setCountdown] = useState(5); // Countdown state initialized to 5 seconds
    const cancelledRef = useRef(false); // Ref to track if the redirection has been cancelled
    // Get the replacement text for the countdown value
    const replacementValue = getReplacementText(countdown);
    // Generate the redirection text with the countdown value
    const redirectionText = OpenBalanceTexts.REDIRECTING.replace("{countdown}", replacementValue);
    // If there is no balance and a payment has not been completed, the order is already paid for.
    const messageText = OBP.isAlreadyPaid ? OpenBalanceTexts.ALREADY_PAID : OpenBalanceTexts.PAYMENT_COMPLETE;
    // Effect to handle cancellation of redirection when the user navigates away or reloads the page.
    // This ensures that the user is not redirected when they are navigating away/have already navigated away from the page.
    useEffect(() => {
        const handleBeforeUnload = () => {
            cancelledRef.current = true; // Mark redirection as cancelled
        };
        // Add event listener for the beforeunload event
        window.addEventListener("beforeunload", handleBeforeUnload);
        // Cleanup function to remove the event listener and mark redirection as cancelled
        return () => {
            cancelledRef.current = true;
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);
    // Effect to handle the countdown and redirection logic
    useEffect(() => {
        // If countdown reaches 0 and redirection is not cancelled, navigate to the orders page
        if (countdown <= 0) {
            if (!cancelledRef.current) {
                redirectToOrders(navigate);
            }
            return;
        }
        // Set a timer to decrement the countdown every second
        const timer = setTimeout(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);
        // Cleanup function to clear the timer
        return () => clearTimeout(timer);
    }, [countdown, navigate]);
    // Return the message text for display
    return {
        messageText,
        redirectionText,
        isAlreadyPaid: OBP.isAlreadyPaid,
    };
};
